import type { EventHint } from '@sentry/vue'
import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import { Router } from '@/helpers/router.ts'
import type { User } from '@sentry/browser'
import type { UsePermissionReturn } from '@vueuse/core'
import { useEventBus, useWindowSize } from '@vueuse/core'
import type { WhoAmI } from '@/stores'
import { useAuthStore } from '@/stores'
import type { AxiosError, AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'
import { isEmpty } from 'lodash-es'
import type { ReportDialogOptions } from '@sentry/core'
import { RoleHelper } from '@/helpers/RoleHelper.ts'
import { zipObject } from 'lodash'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'
import { client } from '@/api/client.gen.ts'

const TitropSentry = new (class {
  readonly #enabled: Ref<boolean> = ref<boolean>(!!__SENTRY_DSN__)
  #inited = false

  get Enabled(): Ref<boolean> {
    return this.#enabled
  }

  Enable(): void {
    this.#enabled.value = true
    Sentry.getClient()?.init()
  }

  Disable(): void {
    void Sentry.getClient()?.close()
    this.#enabled.value = false
  }

  ReportDialogOptions(onLoadCb?: () => void): ReportDialogOptions {
    let u: ReportDialogOptions['user'] = Sentry.getCurrentScope().getUser()
    if (isEmpty(u)) {
      const wai = useAuthStore().whoami
      if (wai) {
        u = {
          email: wai.mail ?? undefined,
          name: wai.displayname ?? undefined
        }
      }
    }
    return {
      title: 'Ein Fehler ist aufgetreten',
      subtitle: 'Wir sind bereits informiert worden.',
      subtitle2: '',
      labelComments: 'Was ist passiert?',
      labelClose: 'Schließen',
      labelSubmit: 'Absenden',
      errorGeneric: 'Ein unbekannter Fehler ist beim Senden des Berichts aufgetreten. Bitte erneut versuchen.',
      errorFormEntry: 'Einige Felder waren ungültig. Bitte korrigieren und erneut versuchen.',
      successMessage: 'Der Bericht wurde übermittelt. Vielen Dank!',
      user: u,
      onLoad: () => {
        const inName = document.querySelector('div.sentry-error-embed-wrapper > div > form > div.form-content > div.form-field > input#id_name') as HTMLInputElement
        if (inName.value && inName.parentElement) {
          inName.parentElement.style.display = 'none'
        }
        const inMail = document.querySelector('div.sentry-error-embed-wrapper > div > form > div.form-content > div.form-field > input#id_email') as HTMLInputElement
        if (inMail.value && inMail.parentElement) {
          inMail.parentElement.style.display = 'none'
        }

        const inComments = document.querySelector('div.sentry-error-embed-wrapper > div > form > div.form-content > div.form-field > textarea#id_comments') as HTMLTextAreaElement
        inComments.placeholder = ''
        const poweredBy = document.querySelector('p.powered-by') as HTMLParagraphElement
        poweredBy.style.display = 'none'

        if (onLoadCb) onLoadCb()
      }
    }
  }

  Init(app: App<Element>): void {
    if (this.#inited) {
      console.warn('Sentry client already inited')
      return
    }
    this.#inited = true

    // Kein Setup in Entwicklungsumgebungen ohne DSN
    if (!this.#enabled.value) {
      //no sentry in dev
      console.log('Sentry disabled')
      return
    }
    console.log(`Sentry enabled with ${__SENTRY_DSN__}`)

    // Fehlerbehandlung: Axios-Fehler (inkl. Request/Response-Kontext) an Sentry weitergeben
    client.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const req = error.request.__sentry_xhr_v3__
        const res = error.response
        Sentry.getCurrentScope().setContext('request', req)
        if (res) {
          Sentry.getCurrentScope().setContext('response', {
            data: res.data,
            status: res.status,
            statusText: res.statusText,
            headers: res.headers
          })
        }
        return Promise.reject(error)
      }
    )
    // AuthStore-Änderungen (Pinia) mitverfolgen und in den Sentry-Kontext übernehmen
    useAuthStore().$subscribe(
      (_mutation, state) => {
        Sentry.setContext('auth-store (pinia)', {
          impersonated_user: state.impersonated_user,
          refresh_token: state.refresh_token,
          refresh_token_expiration: state.refresh_token_expiration,
          refreshing: state.refreshing,
          returnUrl: state.returnUrl,
          token: state.token,
          roles: new RoleHelper().AllRoles,
          whoami: state.whoami
        })
      },
      { flush: 'sync', detached: true, deep: true, immediate: true }
    )

    // Bei Navigation: Route-Informationen an Sentry übergeben
    Router.afterEach((currentRoute) => {
      Sentry.setContext('route (router)', {
        fullPath: currentRoute.fullPath,
        name: currentRoute.name,
        params: currentRoute.params,
        query: currentRoute.query,
        hash: currentRoute.hash
      })
    })
    // Bei erfolgter Anmeldung wird der User in den Sentry-Kontext geschrieben
    useEventBus<WhoAmI>('auth.login_success').on((whoami: WhoAmI) => {
      if (!this.#enabled.value) return

      const u: User & { email?: string } = {
        id: whoami['@id'],
        email: whoami.mail ?? undefined,
        username: whoami.displayname,
        name: whoami.displayname,
        ip_address: '{{auto}}'
      }
      Sentry.setUser(u)
    })
    {
      const windowSize = useWindowSize()
      // Sentry.setContext('browser (dimensions)', { height: windowSize.height, width: windowSize.width })
      watch(
        [windowSize.height, windowSize.width],
        ([height, width]) => {
          const supposedDeviceType = (() => {
            const w = window.innerWidth
            if (w < 768) return 'mobile'
            if (w < 1024) return 'tablet'
            return 'desktop'
          })()
          Sentry.setTags({ 'browser.heigth': height, 'browser.width': width, deviceType: supposedDeviceType })
          Sentry.setContext('browser (dimensions)', {
            height,
            width,
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight
            },
            'deviceType (supposed)': supposedDeviceType
          })
        },
        { immediate: true }
      )

      const allPerms = [
        'accelerometer',
        'accessibility-events',
        'ambient-light-sensor',
        'background-sync',
        'camera',
        'clipboard-read',
        'clipboard-write',
        'geolocation',
        'gyroscope',
        'magnetometer',
        'microphone',
        'notifications',
        'payment-handler',
        'persistent-storage',
        'push',
        'screen-wake-lock',
        'speaker',
        'local-fonts',
        'xr-spatial-tracking'
      ]
      const permissions: UsePermissionReturn[] = (allPerms as Parameters<typeof usePermission>[0][]).map((p) => usePermission(p))
      watch(permissions, (arr) => {
        Sentry.setContext('browser (permissions)', zipObject(allPerms, arr))
      })

      const memory = useMemory()
      if (memory.isSupported) {
        watch(
          memory.memory,
          (memory) => {
            Sentry.setContext('browser (memory)', {
              totalJSHeapSize: memory?.totalJSHeapSize,
              jsHeapSizeLimit: memory?.jsHeapSizeLimit,
              usedJSHeapSize: memory?.usedJSHeapSize
            })
          },
          { immediate: true }
        )
      }

      watch(
        usePreferredLanguages(),
        (langs) => {
          Sentry.setTag('browser.pref_lang', langs[0])
          Sentry.setContext('browser (preferred languages)', { ...langs })
        },
        { immediate: true }
      )

      const screenOrientation = useScreenOrientation()
      if (screenOrientation.isSupported) {
        watch(
          [screenOrientation.orientation, screenOrientation.angle],
          ([orientation, angle]) => {
            Sentry.setTag('screen.orientation', orientation)
            Sentry.setContext('screen (orientation)', { orientation, angle })
          },
          { immediate: true }
        )
      }
    }

    // Bei Abmeldung wird der User im Sentry zurückgesetzt
    useTitropEvents().onLogout.on(() => {
      if (!this.#enabled.value) {
        Sentry.setUser(null)
        return
      }
    })

    // Initialisierung des Sentry-Clients mit Konfiguration und Integrationen
    Sentry.init({
      normalizeDepth: 10,
      beforeBreadcrumb: (breadcrumb) => {
        if (breadcrumb.category == 'sentry.transaction') return breadcrumb
        if (breadcrumb.category == 'xhr') return breadcrumb
        if (breadcrumb.category == 'navigation') return breadcrumb
        if (breadcrumb.category == 'pinia.action') return null
        if (breadcrumb.message?.startsWith('🍍 ')) return null
        // debugger
        return breadcrumb
      },
      app: app,

      dsn: __SENTRY_DSN__,
      sendDefaultPii: false,
      integrations: [
        Sentry.vueIntegration({ app: app, attachProps: true, tracingOptions: { trackComponents: true } }),
        // Sentry.browserTracingIntegration({
        //     router: Router,
        //     trackFetchStreamPerformance: false,
        //     traceXHR: true,
        //     traceFetch: true
        // }),
        // Sentry.browserProfilingIntegration(),
        Sentry.browserApiErrorsIntegration(),
        Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
        Sentry.httpContextIntegration(),

        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false
        }),
        Sentry.browserTracingIntegration({ router: Router }),
        Sentry.browserProfilingIntegration()
      ],
      release: __BUILD_VERSION__,
      environment: __MODE__,
      beforeSend: (event: Sentry.ErrorEvent, hint: EventHint) => {
        if (!this.#enabled.value) return null
        if (event.exception?.values && event.exception.values[0]?.type == 'AxiosError' && isAxiosError(hint.originalException) && hint.originalException.config?.url) {
          if (hint.originalException.config.url == '/api/whoami') return null //dont log whoami-errors on expired tokens
          event.exception.values[0].value += ` (${hint.originalException.config.url})`
        }
        return event
      },
      tracesSampleRate: 1.0,
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: ['localhost', /^.*titrop\.com/],
      // Set profilesSampleRate to 1.0 to profile every transaction.
      // Since profilesSampleRate is relative to tracesSampleRate,
      // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
      // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
      // result in 25% of transactions being profiled (0.5*0.5=0.25)
      profilesSampleRate: 1.0,
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })
  }

  setUser(user: User | null): void {
    Sentry.setUser(user)
  }
})()
export default TitropSentry
